import { useState } from "react";
import { Copy, GripVertical, MoreHorizontal, Plus, Trash2, X } from "lucide-react";
import CardItem from "@/components/board/CardItem";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Column({
  list,
  lists, // eslint-disable-line no-unused-vars
  cards,
  onAddCard,
  onCardClick,
  onDeleteList,
  onDuplicateList,
  onRenameList,
  onDragStartCard,
  onDragEndCard,
  onDropCard,
  onDragStartList,
  onDragEndList,
  onDropList,
  onMoveCard, // eslint-disable-line no-unused-vars
  draggingCardId,
  draggingListId,
  dragType,
}) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(list.title);
  const [cardDropTarget, setCardDropTarget] = useState(null); // { cardId, placement: 'top' | 'bottom' } | null
  const [isOverBottom, setIsOverBottom] = useState(false);
  const [isOverTopZone, setIsOverTopZone] = useState(false);
  const [columnDropPlacement, setColumnDropPlacement] = useState(null); // 'left' | 'right' | null

  const isDraggingThisList = draggingListId === list.id;

  const submit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    onAddCard(list.id, t);
    setText("");
    setAdding(false);
  };

  const submitRename = (e) => {
    e.preventDefault();
    const t = title.trim();
    if (t && t !== list.title) onRenameList(list.id, t);
    setRenaming(false);
  };

  // Card drag-over handler for individual CardItems
  const handleCardDragOver = (e, card) => {
    if (dragType !== "card") return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const placement = e.clientY < midY ? "top" : "bottom";
    setCardDropTarget({ cardId: card.id, placement });
    setIsOverTopZone(false);
    setIsOverBottom(false);
  };

  const handleCardDragLeave = (e, card) => {
    if (dragType !== "card") return;
    if (cardDropTarget?.cardId !== card.id) return;
    // Keep the target if the pointer is still over this card (moving to a child element)
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    // Pointer left the card → hand off to the container's bottom zone so dropping
    // in the empty space below the cards lands at the end of the list.
    setCardDropTarget(null);
    setIsOverBottom(true);
    setIsOverTopZone(false);
  };

  const handleCardDrop = (e, card) => {
    if (dragType !== "card") return;
    e.preventDefault();
    e.stopPropagation();
    const placement = cardDropTarget?.placement || "top";
    setCardDropTarget(null);
    setIsOverTopZone(false);
    setIsOverBottom(false);
    onDropCard(list.id, card.id, placement);
  };

  // Container drop handler (for empty lists or bottom space)
  const handleContainerDragOver = (e) => {
    if (dragType === "list") {
      if (draggingListId && draggingListId !== list.id) {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const midX = rect.left + rect.width / 2;
        setColumnDropPlacement(e.clientX < midX ? "left" : "right");
      }
      return;
    }

    if (dragType === "card") {
      e.preventDefault();
      // If we are not hovering directly over a specific card
      if (!cardDropTarget) {
        setIsOverBottom(true);
      }
    }
  };

  const handleContainerDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setCardDropTarget(null);
      setIsOverBottom(false);
      setIsOverTopZone(false);
      setColumnDropPlacement(null);
    }
  };

  const handleContainerDrop = (e) => {
    e.preventDefault();
    if (dragType === "list") {
      if (columnDropPlacement) {
        const placement = columnDropPlacement;
        setColumnDropPlacement(null);
        onDropList && onDropList(list.id, placement);
      }
      return;
    }

    if (dragType === "card") {
      const dropTarget = cardDropTarget;
      const wasTopZone = isOverTopZone;
      setCardDropTarget(null);
      setIsOverBottom(false);
      setIsOverTopZone(false);

      if (dropTarget) {
        onDropCard(list.id, dropTarget.cardId, dropTarget.placement);
      } else if (wasTopZone || cards.length === 0) {
        onDropCard(list.id, null, "top");
      } else {
        onDropCard(list.id, null, "bottom");
      }
    }
  };

  return (
    <div className="flex items-stretch shrink-0 snap-start sm:snap-align-none h-full">
      {/* Column insertion indicator (left) */}
      {columnDropPlacement === "left" && dragType === "list" && (
        <div
          className="w-1.5 bg-black dark:bg-white self-stretch rounded-full mx-1 shadow-sm shrink-0 transition-all animate-pulse"
          data-testid={`column-drop-indicator-left-${list.id}`}
        />
      )}

      <div
        className={`w-full sm:w-[320px] min-w-full sm:min-w-[320px] flex flex-col border-r border-zinc-200 dark:border-zinc-800 h-full bg-zinc-50/30 dark:bg-zinc-900/20 shrink-0 transition-opacity ${
          isDraggingThisList ? "opacity-30 border-dashed border-zinc-400 dark:border-zinc-600" : ""
        }`}
        data-testid={`kanban-column-${list.id}`}
        onDragOver={handleContainerDragOver}
        onDragLeave={handleContainerDragLeave}
        onDrop={handleContainerDrop}
      >
        {/* Column Header */}
        <div
          draggable={!renaming}
          onDragStart={(e) => onDragStartList && onDragStartList(e, list)}
          onDragEnd={onDragEndList}
          className="px-3 py-3 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shrink-0 cursor-grab active:cursor-grabbing select-none group/header"
        >
          <div className="flex items-center gap-1.5 flex-1 min-w-0 mr-2">
            <GripVertical className="h-4 w-4 text-zinc-300 dark:text-zinc-600 group-hover/header:text-zinc-500 dark:group-hover/header:text-zinc-400 transition-colors shrink-0" />
            {renaming ? (
              <form onSubmit={submitRename} className="flex-1">
                <input
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={submitRename}
                  className="w-full text-sm font-bold uppercase tracking-[0.1em] border-b border-black bg-transparent focus:outline-none"
                  data-testid={`column-rename-input-${list.id}`}
                />
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setRenaming(true)}
                className="text-sm font-bold uppercase tracking-[0.1em] text-zinc-950 dark:text-zinc-100 hover:text-zinc-500 dark:hover:text-zinc-400 transition-colors truncate text-left"
                data-testid={`column-title-${list.id}`}
              >
                {list.title}
                <span className="ml-2 text-zinc-400 font-mono-tight">{cards.length}</span>
              </button>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="p-2 -m-1 text-zinc-500 hover:text-black dark:hover:text-white active:text-black transition-colors shrink-0"
                data-testid={`column-menu-${list.id}`}
                aria-label="Column menu"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none border-zinc-200 dark:border-zinc-800">
              <DropdownMenuItem onClick={() => setRenaming(true)}>Rename</DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDuplicateList(list.id)}
                data-testid={`column-duplicate-${list.id}`}
              >
                <Copy className="h-3.5 w-3.5 mr-2" /> Duplicate list
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  if (window.confirm("Delete this list and all its cards?")) onDeleteList(list.id);
                }}
                className="text-red-600 focus:text-red-600"
                data-testid={`column-delete-${list.id}`}
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete list
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Cards Container */}
        <div
          className={`flex-1 overflow-y-auto p-3 flex flex-col gap-2 transition-colors ${
            isOverBottom && dragType === "card" && !cardDropTarget
              ? "bg-zinc-100/80 dark:bg-zinc-800/40"
              : ""
          }`}
        >
          {/* Top Drop Zone (Allows seamlessly dropping at index 0 / pinakataas) */}
          {cards.length > 0 && dragType === "card" && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOverTopZone(true);
                setCardDropTarget(null);
                setIsOverBottom(false);
              }}
              onDragLeave={() => setIsOverTopZone(false)}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOverTopZone(false);
                onDropCard(list.id, null, "top");
              }}
              className="h-2 -mt-1 -mb-1 flex items-center justify-center cursor-pointer"
              data-testid={`column-top-dropzone-${list.id}`}
            >
              {isOverTopZone && (
                <div
                  className="h-1.5 w-full bg-black dark:bg-white rounded-full shadow-sm animate-pulse"
                  data-testid={`top-drop-indicator-${list.id}`}
                />
              )}
            </div>
          )}

          {/* Card Items */}
          {cards.map((c) => (
            <CardItem
              key={c.id}
              card={c}
              onClick={() => onCardClick(c)}
              onDragStart={onDragStartCard}
              onDragEnd={onDragEndCard}
              isDragging={draggingCardId === c.id}
              onDragOver={handleCardDragOver}
              onDragLeave={handleCardDragLeave}
              onDrop={handleCardDrop}
              dropPlacement={cardDropTarget?.cardId === c.id ? cardDropTarget.placement : null}
            />
          ))}

          {/* Empty list drop placeholder / indicator */}
          {cards.length === 0 && dragType === "card" && (
            <div
              className={`border-2 border-dashed rounded p-4 text-center text-xs transition-colors ${
                isOverBottom
                  ? "border-black dark:border-white bg-zinc-100 dark:bg-zinc-800/60 text-zinc-950 dark:text-zinc-100"
                  : "border-zinc-300 dark:border-zinc-700 text-zinc-400"
              }`}
              data-testid={`empty-list-dropzone-${list.id}`}
            >
              Drop card at the top
            </div>
          )}

          {/* Bottom Drop Zone Indicator when hovering below cards */}
          {cards.length > 0 && isOverBottom && dragType === "card" && !cardDropTarget && !isOverTopZone && (
            <div
              className="h-1 bg-black dark:bg-white rounded-full mx-0.5 shadow-sm transition-all animate-pulse"
              data-testid={`bottom-drop-indicator-${list.id}`}
            />
          )}

          {/* Add card form / button */}
          {adding ? (
            <form
              onSubmit={submit}
              className="border border-black dark:border-white bg-white dark:bg-zinc-950 p-2 mt-1"
              data-testid={`add-card-form-${list.id}`}
            >
              <textarea
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={2}
                placeholder="Card title..."
                className="w-full text-base sm:text-sm resize-none focus:outline-none bg-transparent text-zinc-950 dark:text-zinc-100"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit(e);
                  }
                }}
                data-testid={`add-card-input-${list.id}`}
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="submit"
                  className="bg-black dark:bg-white text-white dark:text-black px-3 py-2 text-xs font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200"
                  data-testid={`add-card-submit-${list.id}`}
                >
                  Add card
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAdding(false);
                    setText("");
                  }}
                  className="p-2 text-zinc-500 hover:text-black dark:hover:text-white"
                  aria-label="Cancel"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="text-xs text-zinc-500 hover:text-black dark:hover:text-white active:text-black transition-colors flex items-center gap-1.5 py-3 px-1 text-left mt-auto"
              data-testid={`add-card-button-${list.id}`}
            >
              <Plus className="h-3.5 w-3.5" /> Add a card
            </button>
          )}
        </div>
      </div>

      {/* Column insertion indicator (right) */}
      {columnDropPlacement === "right" && dragType === "list" && (
        <div
          className="w-1.5 bg-black dark:bg-white self-stretch rounded-full mx-1 shadow-sm shrink-0 transition-all animate-pulse"
          data-testid={`column-drop-indicator-right-${list.id}`}
        />
      )}
    </div>
  );
}
